import React, { useCallback, useEffect, useState, Component } from 'react';
import ReactDOM from 'react-dom';
import { Plus, Search, Eye, Pencil, MoreHorizontal, X, AlertTriangle, ChevronLeft, ChevronRight, Loader2, Check } from 'lucide-react';
import { useAuth } from '../store/authStore';
import { getAllUsers, createUser, updateUserStatus, deleteUser } from '../api/users';
// --- Types ---
interface User {
  id: string;
  fullName: string;
  email: string;
  phone?: string;
  status: 'active' | 'inactive' | 'suspended';
  joinedAt: string;
  deviceCount: number;
}
// --- Main Component ---
export function Users() {
  const {
    token
  } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  // Filters & Pagination
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortFilter, setSortFilter] = useState('newest');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  // Selection
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  // Dropdown menu state
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [menuPos, setMenuPos] = useState<{
    top: number;
    left: number;
  } | null>(null);
  // Modals & Panels State
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editUser, setEditUser] = useState<User | null>(null);
  const [deleteUserObj, setDeleteUserObj] = useState<User | null>(null);
  const [viewUser, setViewUser] = useState<User | null>(null);
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);
  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(timer);
  }, [search]);
  // Fetch Data
  const fetchUsers = useCallback(async () => {
    if (!token) return;
    setIsLoading(true);
    try {
      const filters = {
        search: debouncedSearch,
        status: statusFilter === 'all' ? undefined : statusFilter,
        sort: sortFilter,
        page,
        limit
      };
      const res = await getAllUsers(token, filters);
      const rawUsers = res.users || res.data || [];
      setUsers(rawUsers.map((u: any) => ({
        id: u.id,
        fullName: u.fullName || '',
        email: u.email || '',
        phone: u.phone,
        status: u.status || 'active',
        joinedAt: u.createdAt || u.joinedAt || '',
        deviceCount: u._count?.deviceAssignments ?? u.deviceCount ?? 0
      })));
      setTotalUsers(res.total || 0);
    } catch (err: any) {
      setError(err.message || 'Failed to load users');
    } finally {
      setIsLoading(false);
    }
  }, [token, debouncedSearch, statusFilter, sortFilter, page, limit]);
  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);
  // Handlers
  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedIds(new Set(users.map(u => u.id)));
    } else {
      setSelectedIds(new Set());
    }
  };
  const handleSelectOne = (id: string) => {
    const newSet = new Set(selectedIds);
    if (newSet.has(id)) newSet.delete(id);else newSet.add(id);
    setSelectedIds(newSet);
  };
  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({
      message,
      type
    });
    setTimeout(() => setToast(null), 3000);
  };
  const handleBulkAction = async (action: 'activate' | 'suspend' | 'delete') => {
    if (!token) return;
    try {
      if (action === 'delete') {
        // In a real app, you'd want a bulk delete endpoint or Promise.all
        await Promise.all(Array.from(selectedIds).map(id => deleteUser(token, id)));
        showToast(`Deleted ${selectedIds.size} users`);
      } else {
        const newStatus = action === 'activate' ? 'active' : 'suspended';
        await Promise.all(Array.from(selectedIds).map(id => updateUserStatus(token, id, newStatus)));
        showToast(`Updated ${selectedIds.size} users to ${newStatus}`);
      }
      setSelectedIds(new Set());
      fetchUsers();
    } catch (err: any) {
      showToast(err.message || 'Bulk action failed', 'error');
    }
  };
  return <div className="relative min-h-full pb-20" data-id="element-1064">
      {/* Header */}
      <div className="flex justify-between items-center mb-6" data-id="element-1065">
        <div data-id="element-1066">
          <h1 className="text-[24px] font-semibold text-white" data-id="element-1067">Users</h1>
          <p className="text-[14px] text-text-secondary mt-1" data-id="element-1068">
            {isLoading ? 'Loading...' : `${totalUsers.toLocaleString()} total users`}
          </p>
        </div>
        <button onClick={() => setIsAddModalOpen(true)} className="h-[36px] px-4 bg-accent text-background font-semibold rounded-btn flex items-center gap-2 hover:opacity-90 transition-opacity" data-id="element-1069">
          <Plus className="w-4 h-4" data-id="element-1070" />
          Add User
        </button>
      </div>

      {/* Filters Bar */}
      <div className="flex items-center justify-between mb-6 gap-4" data-id="element-1071">
        <div className="flex items-center gap-3" data-id="element-1072">
          <div className="relative" data-id="element-1073">
            <Search className="w-4 h-4 text-text-tertiary absolute left-3 top-1/2 -translate-y-1/2" data-id="element-1074" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name or email..." className="w-[280px] h-[36px] bg-surface border border-border rounded-input pl-9 pr-4 text-[14px] text-white placeholder:text-text-tertiary focus:outline-none focus:border-accent transition-colors" data-id="element-1075" />
          </div>

          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="h-[36px] bg-surface border border-border rounded-input px-3 text-[14px] text-white focus:outline-none focus:border-accent appearance-none pr-8 relative" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%238A8A8A' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'right 8px center'
        }} data-id="element-1076">
            <option value="all" data-id="element-1077">All Status</option>
            <option value="active" data-id="element-1078">Active</option>
            <option value="inactive" data-id="element-1079">Inactive</option>
            <option value="suspended" data-id="element-1080">Suspended</option>
          </select>

          <select value={sortFilter} onChange={e => setSortFilter(e.target.value)} className="h-[36px] bg-surface border border-border rounded-input px-3 text-[14px] text-white focus:outline-none focus:border-accent appearance-none pr-8" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%238A8A8A' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'right 8px center'
        }} data-id="element-1081">
            <option value="newest" data-id="element-1082">Newest first</option>
            <option value="oldest" data-id="element-1083">Oldest first</option>
            <option value="name_asc" data-id="element-1084">Name A-Z</option>
            <option value="name_desc" data-id="element-1085">Name Z-A</option>
          </select>
        </div>

        <div className="text-[13px] text-text-secondary" data-id="element-1086">
          Showing {users.length > 0 ? (page - 1) * limit + 1 : 0}-
          {Math.min(page * limit, totalUsers)} of {totalUsers} users
        </div>
      </div>

      {/* Table */}
      <div className="bg-surface border border-border rounded-card overflow-hidden" data-id="element-1087">
        <div className="overflow-x-auto" data-id="element-1088">
          <table className="w-full text-left border-collapse" data-id="element-1089">
            <thead data-id="element-1090">
              <tr className="bg-surface-elevated border-b border-border h-[44px]" data-id="element-1091">
                <th className="px-4 w-[40px]" data-id="element-1092">
                  <div className="relative flex items-center" data-id="element-1093">
                    <input type="checkbox" className="peer sr-only" checked={users.length > 0 && selectedIds.size === users.length} onChange={handleSelectAll} data-id="element-1094" />
                    <div className="w-4 h-4 bg-surface border border-border rounded-[4px] peer-checked:bg-accent peer-checked:border-accent flex items-center justify-center transition-colors" data-id="element-1095">
                      <Check className="w-3 h-3 text-white opacity-0 peer-checked:opacity-100" data-id="element-1096" />
                    </div>
                  </div>
                </th>
                <th className="px-4 text-[12px] uppercase font-semibold text-text-secondary tracking-[1px]" data-id="element-1097">
                  User
                </th>
                <th className="px-4 text-[12px] uppercase font-semibold text-text-secondary tracking-[1px] w-[140px]" data-id="element-1098">
                  Phone
                </th>
                <th className="px-4 text-[12px] uppercase font-semibold text-text-secondary tracking-[1px] w-[100px]" data-id="element-1099">
                  Status
                </th>
                <th className="px-4 text-[12px] uppercase font-semibold text-text-secondary tracking-[1px] w-[120px]" data-id="element-1100">
                  Joined
                </th>
                <th className="px-4 text-[12px] uppercase font-semibold text-text-secondary tracking-[1px] w-[80px]" data-id="element-1101">
                  Devices
                </th>
                <th className="px-4 text-[12px] uppercase font-semibold text-text-secondary tracking-[1px] w-[100px]" data-id="element-1102">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody data-id="element-1103">
              {isLoading ? <tr data-id="element-1104">
                  <td colSpan={7} className="h-[200px] text-center text-text-secondary" data-id="element-1105">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" data-id="element-1106" />
                    Loading users...
                  </td>
                </tr> : users.length === 0 ? <tr data-id="element-1107">
                  <td colSpan={7} className="h-[200px] text-center text-text-secondary" data-id="element-1108">
                    No users found matching your criteria.
                  </td>
                </tr> : users.map(user => <tr key={user.id} className="h-[56px] border-b border-[#1E1E1E] hover:bg-[#1E1E1E] transition-colors" data-id="element-1109">
                    <td className="px-4" data-id="element-1110">
                      <div className="relative flex items-center" data-id="element-1111">
                        <input type="checkbox" className="peer sr-only" checked={selectedIds.has(user.id)} onChange={() => handleSelectOne(user.id)} data-id="element-1112" />
                        <div className="w-4 h-4 bg-surface border border-border rounded-[4px] peer-checked:bg-accent peer-checked:border-accent flex items-center justify-center transition-colors" data-id="element-1113">
                          <Check className="w-3 h-3 text-white opacity-0 peer-checked:opacity-100" data-id="element-1114" />
                        </div>
                      </div>
                    </td>
                    <td className="px-4" data-id="element-1115">
                      <div className="flex items-center gap-3" data-id="element-1116">
                        <div className="w-8 h-8 rounded-full bg-accent flex items-center justify-center text-background text-[12px] font-semibold flex-shrink-0" data-id="element-1117">
                          {user.fullName.charAt(0).toUpperCase()}
                        </div>
                        <div className="flex flex-col" data-id="element-1118">
                          <span className="text-[14px] font-medium text-white" data-id="element-1119">
                            {user.fullName}
                          </span>
                          <span className="text-[13px] text-text-secondary" data-id="element-1120">
                            {user.email}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 text-[13px] text-text-secondary" data-id="element-1121">
                      {user.phone || '—'}
                    </td>
                    <td className="px-4" data-id="element-1122">
                      {user.status === 'active' && <span className="px-2 py-0.5 rounded-badge bg-[rgba(34,197,94,0.1)] text-success text-[12px] border border-[rgba(34,197,94,0.2)]" data-id="element-1123">
                          Active
                        </span>}
                      {user.status === 'inactive' && <span className="px-2 py-0.5 rounded-badge bg-[rgba(138,138,138,0.1)] text-text-secondary text-[12px]" data-id="element-1124">
                          Inactive
                        </span>}
                      {user.status === 'suspended' && <span className="px-2 py-0.5 rounded-badge bg-[rgba(239,68,68,0.1)] text-danger text-[12px] border border-[rgba(239,68,68,0.2)]" data-id="element-1125">
                          Suspended
                        </span>}
                    </td>
                    <td className="px-4 text-[13px] text-text-secondary" data-id="element-1126">
                      {new Date(user.joinedAt).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric'
                })}
                    </td>
                    <td className="px-4 text-[13px] text-white" data-id="element-1127">
                      {user.deviceCount > 0 ? user.deviceCount : <span className="text-text-tertiary" data-id="element-1128">0</span>}
                    </td>
                    <td className="px-4" data-id="element-1129">
                      <div className="flex items-center gap-1" data-id="element-1130">
                        <button onClick={() => setViewUser(user)} className="w-7 h-7 rounded-full flex items-center justify-center hover:bg-border transition-colors text-text-tertiary hover:text-white" data-id="element-1131">
                          <Eye className="w-4 h-4" data-id="element-1132" />
                        </button>
                        <button onClick={() => setEditUser(user)} className="w-7 h-7 rounded-full flex items-center justify-center hover:bg-border transition-colors text-text-tertiary hover:text-white" data-id="element-1133">
                          <Pencil className="w-4 h-4" data-id="element-1134" />
                        </button>
                        <button onClick={e => {
                    e.stopPropagation();
                    if (openMenuId === user.id) {
                      setOpenMenuId(null);
                      setMenuPos(null);
                    } else {
                      const rect = e.currentTarget.getBoundingClientRect();
                      setMenuPos({
                        top: rect.bottom + 4,
                        left: rect.right - 176
                      });
                      setOpenMenuId(user.id);
                    }
                  }} className="w-7 h-7 rounded-full flex items-center justify-center hover:bg-border transition-colors text-text-tertiary hover:text-white" data-id="element-1135">
                          <MoreHorizontal className="w-4 h-4" data-id="element-1136" />
                        </button>
                      </div>
                    </td>
                  </tr>)}
            </tbody>
          </table>
        </div>
      </div>

      {/* Dropdown Menu Portal - rendered outside table to avoid overflow clipping */}
      {openMenuId && menuPos && ReactDOM.createPortal(<>
            <div className="fixed inset-0" style={{
        zIndex: 99999
      }} onClick={() => {
        setOpenMenuId(null);
        setMenuPos(null);
      }} data-id="element-1137" />
            <div className="fixed w-44 bg-surface-elevated border border-border rounded-card py-1" style={{
        zIndex: 100000,
        top: menuPos.top,
        left: menuPos.left
      }} data-id="element-1138">
              <button onClick={() => {
          const u = users.find(usr => usr.id === openMenuId);
          if (u) {
            updateUserStatus(token!, u.id, u.status === 'suspended' ? 'active' : 'suspended');
            fetchUsers();
          }
          setOpenMenuId(null);
          setMenuPos(null);
        }} className="w-full text-left px-4 py-2 text-[13px] text-white hover:bg-border first:rounded-t-card" data-id="element-1139">
                {users.find(u => u.id === openMenuId)?.status === 'suspended' ? 'Activate Account' : 'Suspend Account'}
              </button>
              <button onClick={() => {
          const u = users.find(usr => usr.id === openMenuId);
          if (u) setDeleteUserObj(u);
          setOpenMenuId(null);
          setMenuPos(null);
        }} className="w-full text-left px-4 py-2 text-[13px] text-danger hover:bg-border last:rounded-b-card" data-id="element-1140">
                Delete User
              </button>
            </div>
          </>, document.body)}

      {/* Pagination */}
      <div className="flex items-center justify-between mt-4" data-id="element-1141">
        <div className="flex items-center gap-2" data-id="element-1142">
          <select value={limit} onChange={e => {
          setLimit(Number(e.target.value));
          setPage(1);
        }} className="h-[32px] bg-surface border border-border rounded-btn px-2 text-[13px] text-white focus:outline-none focus:border-accent" data-id="element-1143">
            <option value={25} data-id="element-1144">25 per page</option>
            <option value={50} data-id="element-1145">50 per page</option>
            <option value={100} data-id="element-1146">100 per page</option>
          </select>
        </div>

        <div className="flex items-center gap-2" data-id="element-1147">
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="h-[32px] px-3 bg-surface border border-border rounded-btn text-[13px] text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-border transition-colors flex items-center gap-1" data-id="element-1148">
            <ChevronLeft className="w-4 h-4" data-id="element-1149" /> Prev
          </button>

          <div className="flex items-center gap-1" data-id="element-1150">
            {/* Simplified page numbers for now */}
            <button className="w-[32px] h-[32px] bg-accent text-background rounded-btn text-[13px] font-medium flex items-center justify-center" data-id="element-1151">
              {page}
            </button>
          </div>

          <button onClick={() => setPage(p => p + 1)} disabled={page * limit >= totalUsers} className="h-[32px] px-3 bg-surface border border-border rounded-btn text-[13px] text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-border transition-colors flex items-center gap-1" data-id="element-1152">
            Next <ChevronRight className="w-4 h-4" data-id="element-1153" />
          </button>
        </div>
      </div>

      {/* Bulk Actions Bar */}
      <div className={`fixed bottom-6 left-1/2 -translate-x-1/2 bg-surface-elevated border border-border rounded-card px-5 py-3 flex items-center gap-4 shadow-2xl transition-transform duration-300 z-20 ${selectedIds.size > 0 ? 'translate-y-0' : 'translate-y-[100px]'}`} data-id="element-1154">
        <span className="text-[14px] text-white font-medium" data-id="element-1155">
          {selectedIds.size} users selected
        </span>
        <div className="w-[1px] h-6 bg-border" data-id="element-1156" />
        <button onClick={() => handleBulkAction('activate')} className="px-3 py-1.5 border border-success text-success rounded-btn text-[13px] font-medium hover:bg-[rgba(34,197,94,0.1)] transition-colors" data-id="element-1157">
          Activate
        </button>
        <button onClick={() => handleBulkAction('suspend')} className="px-3 py-1.5 border border-warning text-warning rounded-btn text-[13px] font-medium hover:bg-[rgba(245,158,11,0.1)] transition-colors" data-id="element-1158">
          Suspend
        </button>
        <button onClick={() => handleBulkAction('delete')} className="px-3 py-1.5 border border-danger text-danger rounded-btn text-[13px] font-medium hover:bg-[rgba(239,68,68,0.1)] transition-colors" data-id="element-1159">
          Delete
        </button>
        <button onClick={() => setSelectedIds(new Set())} className="px-3 py-1.5 text-text-secondary text-[13px] hover:text-white transition-colors" data-id="element-1160">
          Clear selection
        </button>
      </div>

      {/* Toast */}
      {toast && <div className={`fixed top-6 right-6 px-4 py-3 rounded-badge shadow-lg flex items-center gap-2 z-50 animate-in slide-in-from-top-5 ${toast.type === 'success' ? 'bg-success text-white' : 'bg-danger text-white'}`} data-id="element-1161">
          {toast.type === 'success' ? <Check className="w-4 h-4" data-id="element-1162" /> : <AlertTriangle className="w-4 h-4" data-id="element-1163" />}
          <span className="text-[14px] font-medium" data-id="element-1164">{toast.message}</span>
        </div>}

      {/* Modals & Panels */}
      <UserModal isOpen={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} onSuccess={() => {
      setIsAddModalOpen(false);
      showToast('User created successfully');
      fetchUsers();
    }} data-id="element-1165" />

      {editUser && <UserModal isOpen={true} user={editUser} onClose={() => setEditUser(null)} onSuccess={() => {
      setEditUser(null);
      showToast('User updated successfully');
      fetchUsers();
    }} data-id="element-1166" />}

      {deleteUserObj && <DeleteModal user={deleteUserObj} onClose={() => setDeleteUserObj(null)} onConfirm={async () => {
      if (!token) return;
      try {
        await deleteUser(token, deleteUserObj.id);
        setDeleteUserObj(null);
        showToast('User deleted successfully');
        fetchUsers();
      } catch (err: any) {
        showToast(err.message || 'Failed to delete user', 'error');
      }
    }} data-id="element-1167" />}

      <UserDetailPanel user={viewUser} onClose={() => setViewUser(null)} onEdit={() => {
      setViewUser(null);
      setEditUser(viewUser);
    }} onDelete={() => {
      setViewUser(null);
      setDeleteUserObj(viewUser);
    }} data-id="element-1168" />
    </div>;
}
// --- Subcomponents ---
function UserModal({
  isOpen,
  user,
  onClose,
  onSuccess
}: {
  isOpen: boolean;
  user?: User;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const {
    token
  } = useAuth();
  const [formData, setFormData] = useState({
    fullName: user?.fullName || '',
    email: user?.email || '',
    phone: user?.phone || '',
    password: '',
    status: user?.status || 'active'
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  if (!isOpen) return null;
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;
    setIsLoading(true);
    setError('');
    try {
      if (user) {
        // Edit mode - just update status for now as per API spec
        await updateUserStatus(token, user.id, formData.status);
      } else {
        // Create mode
        await createUser(token, formData);
      }
      onSuccess();
    } catch (err: any) {
      setError(err.message || 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-1169">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-[4px]" onClick={onClose} data-id="element-1170" />
      <div className="relative w-full max-w-[480px] bg-surface border border-border rounded-card p-8 shadow-2xl" data-id="element-1171">
        <button onClick={onClose} className="absolute top-6 right-6 text-text-secondary hover:text-white transition-colors" data-id="element-1172">
          <X className="w-5 h-5" data-id="element-1173" />
        </button>

        <h2 className="text-[18px] font-semibold text-white mb-6" data-id="element-1174">
          {user ? 'Edit User' : 'Add New User'}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4" data-id="element-1175">
          <div data-id="element-1176">
            <label className="block text-[13px] text-text-secondary mb-1.5" data-id="element-1177">
              Full Name
            </label>
            <input type="text" required value={formData.fullName} onChange={e => setFormData({
            ...formData,
            fullName: e.target.value
          })} className="w-full h-[44px] bg-background border border-border rounded-input px-4 text-white focus:outline-none focus:border-accent" data-id="element-1178" />
          </div>
          <div data-id="element-1179">
            <label className="block text-[13px] text-text-secondary mb-1.5" data-id="element-1180">
              Email
            </label>
            <input type="email" required value={formData.email} onChange={e => setFormData({
            ...formData,
            email: e.target.value
          })} className="w-full h-[44px] bg-background border border-border rounded-input px-4 text-white focus:outline-none focus:border-accent" data-id="element-1181" />
          </div>
          <div data-id="element-1182">
            <label className="block text-[13px] text-text-secondary mb-1.5" data-id="element-1183">
              Phone (Optional)
            </label>
            <input type="tel" value={formData.phone} onChange={e => setFormData({
            ...formData,
            phone: e.target.value
          })} className="w-full h-[44px] bg-background border border-border rounded-input px-4 text-white focus:outline-none focus:border-accent" data-id="element-1184" />
          </div>
          {!user && <div data-id="element-1185">
              <label className="block text-[13px] text-text-secondary mb-1.5" data-id="element-1186">
                Password
              </label>
              <input type="password" required minLength={8} value={formData.password} onChange={e => setFormData({
            ...formData,
            password: e.target.value
          })} className="w-full h-[44px] bg-background border border-border rounded-input px-4 text-white focus:outline-none focus:border-accent" data-id="element-1187" />
            </div>}
          <div data-id="element-1188">
            <label className="block text-[13px] text-text-secondary mb-1.5" data-id="element-1189">
              Status
            </label>
            <select value={formData.status} onChange={e => setFormData({
            ...formData,
            status: e.target.value as any
          })} className="w-full h-[44px] bg-background border border-border rounded-input px-4 text-white focus:outline-none focus:border-accent appearance-none" data-id="element-1190">
              <option value="active" data-id="element-1191">Active</option>
              <option value="inactive" data-id="element-1192">Inactive</option>
              <option value="suspended" data-id="element-1193">Suspended</option>
            </select>
          </div>

          {error && <p className="text-danger text-[13px]" data-id="element-1194">{error}</p>}

          <button type="submit" disabled={isLoading} className="w-full h-[44px] bg-accent text-background font-semibold rounded-btn mt-6 flex items-center justify-center hover:opacity-90 disabled:opacity-50" data-id="element-1195">
            {isLoading ? <Loader2 className="w-5 h-5 animate-spin" data-id="element-1196" /> : user ? 'Save Changes' : 'Create User'}
          </button>
        </form>
      </div>
    </div>;
}
function DeleteModal({
  user,
  onClose,
  onConfirm
}: {
  user: User;
  onClose: () => void;
  onConfirm: () => void;
}) {
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-1197">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-[4px]" onClick={onClose} data-id="element-1198" />
      <div className="relative w-full max-w-[360px] bg-surface border border-border rounded-card p-6 text-center shadow-2xl" data-id="element-1199">
        <div className="w-16 h-16 rounded-full bg-[rgba(239,68,68,0.1)] flex items-center justify-center mx-auto mb-4" data-id="element-1200">
          <AlertTriangle className="w-8 h-8 text-danger" data-id="element-1201" />
        </div>
        <h2 className="text-[16px] font-semibold text-white mb-2" data-id="element-1202">
          Delete this user?
        </h2>
        <p className="text-[14px] text-text-secondary mb-6" data-id="element-1203">
          This will permanently delete{' '}
          <strong className="text-white" data-id="element-1204">{user.fullName}</strong> and all their
          data. This cannot be undone.
        </p>
        <div className="flex gap-3" data-id="element-1205">
          <button onClick={onClose} className="flex-1 h-[40px] bg-surface border border-border text-white rounded-btn font-medium hover:bg-border transition-colors" data-id="element-1206">
            Cancel
          </button>
          <button onClick={onConfirm} className="flex-1 h-[40px] bg-danger text-white rounded-btn font-medium hover:opacity-90 transition-opacity" data-id="element-1207">
            Delete
          </button>
        </div>
      </div>
    </div>;
}
function UserDetailPanel({
  user,
  onClose,
  onEdit,
  onDelete
}: {
  user: User | null;
  onClose: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  if (!user) return null;
  return <div className="fixed inset-0 z-40 flex justify-end" data-id="element-1208">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} data-id="element-1209" />
      <div className="relative w-[400px] h-full bg-surface border-l border-border shadow-2xl flex flex-col animate-in slide-in-from-right duration-250" data-id="element-1210">
        {/* Header */}
        <div className="p-6 border-b border-border relative" data-id="element-1211">
          <button onClick={onClose} className="absolute top-6 right-6 text-text-secondary hover:text-white transition-colors" data-id="element-1212">
            <X className="w-5 h-5" data-id="element-1213" />
          </button>
          <div className="flex items-center gap-4" data-id="element-1214">
            <div className="w-12 h-12 rounded-full bg-accent flex items-center justify-center text-background text-[18px] font-semibold" data-id="element-1215">
              {user.fullName.charAt(0).toUpperCase()}
            </div>
            <div data-id="element-1216">
              <h2 className="text-[18px] font-semibold text-white" data-id="element-1217">
                {user.fullName}
              </h2>
              <p className="text-[14px] text-text-secondary" data-id="element-1218">{user.email}</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-8" data-id="element-1219">
          {/* Info */}
          <div className="space-y-4" data-id="element-1220">
            <h3 className="text-[12px] uppercase font-semibold text-text-secondary tracking-[1px]" data-id="element-1221">
              Information
            </h3>
            <div className="space-y-3" data-id="element-1222">
              <div className="flex justify-between" data-id="element-1223">
                <span className="text-[13px] text-text-secondary" data-id="element-1224">ID</span>
                <span className="text-[13px] text-white font-mono" data-id="element-1225">
                  {user.id.substring(0, 8)}...
                </span>
              </div>
              <div className="flex justify-between" data-id="element-1226">
                <span className="text-[13px] text-text-secondary" data-id="element-1227">Phone</span>
                <span className="text-[13px] text-white" data-id="element-1228">
                  {user.phone || '—'}
                </span>
              </div>
              <div className="flex justify-between items-center" data-id="element-1229">
                <span className="text-[13px] text-text-secondary" data-id="element-1230">Status</span>
                {user.status === 'active' && <span className="px-2 py-0.5 rounded-badge bg-[rgba(34,197,94,0.1)] text-success text-[12px] border border-[rgba(34,197,94,0.2)]" data-id="element-1231">
                    Active
                  </span>}
                {user.status === 'inactive' && <span className="px-2 py-0.5 rounded-badge bg-[rgba(138,138,138,0.1)] text-text-secondary text-[12px]" data-id="element-1232">
                    Inactive
                  </span>}
                {user.status === 'suspended' && <span className="px-2 py-0.5 rounded-badge bg-[rgba(239,68,68,0.1)] text-danger text-[12px] border border-[rgba(239,68,68,0.2)]" data-id="element-1233">
                    Suspended
                  </span>}
              </div>
              <div className="flex justify-between" data-id="element-1234">
                <span className="text-[13px] text-text-secondary" data-id="element-1235">Joined</span>
                <span className="text-[13px] text-white" data-id="element-1236">
                  {new Date(user.joinedAt).toLocaleDateString()}
                </span>
              </div>
            </div>
          </div>

          {/* Devices */}
          <div className="space-y-4" data-id="element-1237">
            <h3 className="text-[12px] uppercase font-semibold text-text-secondary tracking-[1px]" data-id="element-1238">
              Devices ({user.deviceCount})
            </h3>
            {user.deviceCount === 0 ? <p className="text-[13px] text-text-tertiary" data-id="element-1239">
                No devices assigned.
              </p> : <p className="text-[13px] text-text-secondary" data-id="element-1240">
                {user.deviceCount} device{user.deviceCount === 1 ? '' : 's'}{' '}
                connected
              </p>}
          </div>
        </div>

        {/* Footer Actions */}
        <div className="p-6 border-t border-border space-y-3" data-id="element-1241">
          <button onClick={onEdit} className="w-full h-[40px] bg-surface border border-border text-white rounded-btn font-medium hover:bg-border transition-colors" data-id="element-1242">
            Edit User
          </button>
          <button onClick={onDelete} className="w-full h-[40px] bg-[rgba(239,68,68,0.1)] text-danger border border-[rgba(239,68,68,0.2)] rounded-btn font-medium hover:bg-[rgba(239,68,68,0.2)] transition-colors" data-id="element-1243">
            Delete User
          </button>
        </div>
      </div>
    </div>;
}